import { useState } from 'react';
import { ROTULO_VISTA } from '../dados/catalogo.js';
import type { Midia, VistaDeFoto } from '../dados/fonte.js';
import { FotoDaMidia } from './FotoDaMidia.js';

const VISTAS: readonly VistaDeFoto[] = ['perfil_esquerdo', 'frente', 'tras'];

/**
 * Estacao 2: as tres fotos da ficha. A de perfil esquerdo e a que mostra os
 * marcadores M1-M18 (angulos por fotogrametria) e vira a capa do animal.
 * A foto fica no aparelho e sobe quando houver rede.
 *
 * Tres colunas tambem no celular: miniaturas quadradas lado a lado, para a
 * estacao caber numa tela em vez de tres fotos vazias empilhadas.
 */
export function FotosDaEstacao2({
  midias,
  editavel,
  adicionar,
  remover,
}: {
  midias: readonly Midia[];
  editavel: boolean;
  adicionar: (vista: VistaDeFoto, arquivo: File) => Promise<unknown>;
  remover: (id: string) => Promise<unknown>;
}) {
  return (
    <ul className="grid grid-cols-3 gap-2 sm:gap-4">
      {VISTAS.map((vista) => (
        <Vaga
          key={vista}
          vista={vista}
          atual={midias.filter((m) => m.kind === 'foto' && m.view === vista).at(-1)}
          editavel={editavel}
          adicionar={adicionar}
          remover={remover}
        />
      ))}
    </ul>
  );
}

function Vaga({
  vista,
  atual,
  editavel,
  adicionar,
  remover,
}: {
  vista: VistaDeFoto;
  atual: Midia | undefined;
  editavel: boolean;
  adicionar: (vista: VistaDeFoto, arquivo: File) => Promise<unknown>;
  remover: (id: string) => Promise<unknown>;
}) {
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  const escolher = async (arquivo: File | undefined) => {
    if (!arquivo) return;
    setErro(null);
    setGravando(true);
    try {
      // A nova primeiro: se ela for recusada (tamanho, tipo), a anterior fica.
      await adicionar(vista, arquivo);
      if (atual) await remover(atual.id);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setGravando(false);
    }
  };

  const situacao = !atual
    ? 'Sem foto'
    : atual.storage_path
      ? 'Enviada'
      : atual.pendente
        ? 'Neste aparelho, aguardando envio'
        : 'Tirada em outro aparelho, ainda não enviada';

  return (
    <li className="min-w-0 space-y-1.5 sm:space-y-2">
      <p className="text-[13px] leading-tight font-medium sm:text-sm">{ROTULO_VISTA[vista]}</p>
      <FotoDaMidia
        midia={atual}
        alt={ROTULO_VISTA[vista]}
        className="aspect-square w-full rounded-(--radius-controle) border border-borda sm:aspect-4/3"
      />
      <p className={`text-xs leading-snug ${atual?.storage_path ? 'text-ok' : 'text-apagado'}`}>{situacao}</p>
      {editavel && (
        <label
          className={`flex min-h-11 w-full cursor-pointer items-center justify-center rounded-(--radius-controle) border border-borda-forte bg-superficie px-2 text-[13px] font-medium select-none hover:border-destaque active:scale-[0.97] sm:inline-flex sm:w-auto sm:px-4 sm:text-sm ${
            gravando ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          {gravando ? 'Guardando…' : atual ? 'Trocar foto' : 'Tirar foto'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(e) => {
              void escolher(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
      )}
      {erro && <p className="text-xs text-erro">{erro}</p>}
    </li>
  );
}
